import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Eye, Clock, CheckCircle, XCircle, Loader, Trash2, Shield } from 'lucide-react';
import { reports as reportsApi } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import Logo from '../components/Logo';

const statusConfig = {
  pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-100', label: 'Pending' },
  processing: { icon: Loader, color: 'text-blue-500', bg: 'bg-blue-100', label: 'Processing' },
  completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100', label: 'Completed' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100', label: 'Failed' }
};

const riskColors = {
  'low': 'bg-green-100 text-green-800',
  'low-moderate': 'bg-teal-100 text-teal-800',
  'moderate': 'bg-yellow-100 text-yellow-800',
  'moderate-high': 'bg-orange-100 text-orange-800',
  'high': 'bg-red-100 text-red-800',
  'extreme': 'bg-red-200 text-red-900'
};

const ReportsList = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedReports, setSelectedReports] = useState(new Set());

  useEffect(() => {
    // Check for success message from navigation
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message after 10 seconds
      setTimeout(() => setSuccessMessage(''), 10000);
      // Clear navigation state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const data = await reportsApi.list();
      setReports(data.reports || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportId, format) => {
    try {
      const blob = await reportsApi.download(reportId, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DD-Report-${reportId}.${format}`;
      a.click();
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  // Auto-refresh: when any report is still processing, schedule a re-fetch in 10s.
  // Uses setTimeout (not setInterval) so the timer resets cleanly after each fetch.
  useEffect(() => {
    const hasProcessing = reports.some(r => r.status === 'processing' || r.status === 'pending');
    if (!hasProcessing) return;
    const timer = setTimeout(loadReports, 10000);
    return () => clearTimeout(timer);
  }, [reports]);

  // Toggle selection for a single report
  const toggleReportSelection = (reportId) => {
    setSelectedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  // Select all completed reports
  const selectAll = () => {
    const completedIds = reports
      .filter(r => r.status === 'completed')
      .map(r => r.reportId);
    setSelectedReports(new Set(completedIds));
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedReports(new Set());
  };

  // Bulk download selected reports
  const handleBulkDownload = async () => {
    if (selectedReports.size === 0) return;

    if (selectedReports.size > 10) {
      alert('Maximum 10 reports per bulk download. Please select fewer reports.');
      return;
    }

    try {
      const reportIds = Array.from(selectedReports);
      const blob = await reportsApi.bulkDownload(reportIds);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DD-Reports-${Date.now()}.zip`;
      a.click();
      clearSelection();
    } catch (err) {
      alert('Bulk download failed: ' + err.message);
    }
  };

  // Delete a single report
  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report? This cannot be undone.')) return;
    try {
      await reportsApi.deleteReport(reportId);
      loadReports();
    } catch (err) {
      alert('Failed to delete report: ' + err.message);
    }
  };

  // Bulk delete selected reports
  const handleBulkDelete = async () => {
    if (selectedReports.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedReports.size} report(s)? This cannot be undone.`)) return;
    try {
      const reportIds = Array.from(selectedReports);
      await reportsApi.bulkDelete(reportIds);
      clearSelection();
      loadReports();
    } catch (err) {
      alert('Failed to delete reports: ' + err.message);
    }
  };

  // Helper function to get status display properties
  const getStatusDisplay = (status) => {
    return statusConfig[status] || statusConfig.pending;
  };

  // Mobile Card View Component
  const MobileReportCard = ({ report, onDownload, onToggleSelection, isSelected }) => {
    const status = getStatusDisplay(report.status);
    const StatusIcon = status.icon;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
        {/* Checkbox for completed reports */}
        {report.status === 'completed' && (
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelection(report.reportId)}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-600">Select this report</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-medium text-gray-900">{report.companyName}</h3>
            <p className="text-xs text-gray-500 font-mono mt-1">{report.reportId}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${status.bg} ${status.color}`}>
            <StatusIcon className="h-3 w-3" />
            {status.label}
          </span>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500 text-xs">Date</span>
            <p className="font-medium">{new Date(report.createdAt).toLocaleDateString('en-GB')}</p>
          </div>
          {report.riskLevel && (
            <div>
              <span className="text-gray-500 text-xs">Risk Level</span>
              <p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${riskColors[report.riskLevel] || 'bg-gray-100'}`}>
                  {report.riskLevel.toUpperCase()}
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <Link
            to={`/reports/${report.reportId}`}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
          >
            <Eye className="h-4 w-4" />
            <span>View</span>
          </Link>
          {report.status === 'completed' && (
            <button
              onClick={() => onDownload(report.reportId, 'docx')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link to="/admin-portal-7x9k2m/dashboard" className="flex items-center gap-1 text-red-700 hover:text-red-900 text-sm font-medium">
                <Shield className="h-4 w-4" />
                Admin
              </Link>
            )}
            <Logo size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Your Reports</h1>
          <Link to="/reports/new" className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
            New Report
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-800 font-medium">Report Created Successfully!</p>
                <p className="text-green-700 text-sm mt-1">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-refresh indicator */}
        {reports.some(r => r.status === 'processing' || r.status === 'pending') && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4 bg-blue-50 border border-blue-200 rounded-md p-3">
            <Loader className="h-4 w-4 animate-spin text-blue-600" />
            <span>Auto-refreshing every 10 seconds while reports are processing... These advanced reports typically take around 1 hour to prepare.</span>
          </div>
        )}

        {/* Bulk Action Toolbar */}
        {selectedReports.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {selectedReports.size} report(s) selected
              </span>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleBulkDownload}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
              >
                <Download className="h-4 w-4" />
                Download ZIP
              </button>

              <button
                onClick={handleBulkDelete}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </button>

              <button
                onClick={clearSelection}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {reports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Reports Yet</h2>
            <p className="text-gray-600 mb-4">Create your first due diligence report to get started.</p>
            <Link to="/reports/new" className="bg-red-600 text-white px-6 py-2 rounded-md inline-block">
              Create Report
            </Link>
          </div>
        ) : (
          <>
            {/* Desktop Table View - Hidden on Mobile */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 w-12">
                        <input
                          type="checkbox"
                          checked={selectedReports.size > 0 && selectedReports.size === reports.filter(r => r.status === 'completed').length}
                          onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reports.map((report) => {
                      const status = statusConfig[report.status] || statusConfig.pending;
                      const StatusIcon = status.icon;
                      return (
                        <tr key={report.reportId} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            {report.status === 'completed' && (
                              <input
                                type="checkbox"
                                checked={selectedReports.has(report.reportId)}
                                onChange={() => toggleReportSelection(report.reportId)}
                                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                              />
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{report.reportId}</td>
                          <td className="px-6 py-4 whitespace-nowrap font-medium">{report.companyName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(report.createdAt).toLocaleDateString('en-GB')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                              <StatusIcon className={`h-3 w-3 ${report.status === 'processing' ? 'animate-spin' : ''}`} />
                              {status.label}
                            </span>
                            {report.statusMessage && (report.status === 'processing' || report.status === 'pending') && (
                              <div className="text-xs text-gray-500 mt-1 max-w-[200px] truncate" title={report.statusMessage}>
                                {report.statusMessage}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {report.riskLevel && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${riskColors[report.riskLevel] || 'bg-gray-100'}`}>
                                {report.riskLevel.toUpperCase()}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                            <Link to={`/reports/${report.reportId}`} className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              View
                            </Link>
                            {report.status === 'completed' && (
                              <button onClick={() => handleDownload(report.reportId, 'docx')} className="text-green-600 hover:text-green-800 inline-flex items-center gap-1">
                                <Download className="h-4 w-4" />
                                Download
                              </button>
                            )}
                            <button onClick={() => handleDelete(report.reportId)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1">
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View - Hidden on Desktop */}
            <div className="md:hidden space-y-4">
              {reports.map((report) => (
                <MobileReportCard
                  key={report.reportId}
                  report={report}
                  onDownload={handleDownload}
                  onToggleSelection={toggleReportSelection}
                  isSelected={selectedReports.has(report.reportId)}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ReportsList;
